import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { LoadMore } from "../components/LoadMore";
import { Row } from "../components/Row";
import { Wordmark } from "../components/Wordmark";
import { api, type Capture } from "../lib/api";
import { Icon } from "../lib/icons";

// Archived captures live off the main timeline (deliberately not a category
// chip). Reached via the discreet link at the bottom of the timeline; you
// unarchive from a capture's detail page.
export function Archived() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const q = useInfiniteQuery({
    queryKey: ["captures", { archived: true }],
    queryFn: ({ pageParam }) => api.listCaptures({ archived: true, before_id: pageParam }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next_before_id ?? undefined,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  const remove = (item: Capture) => {
    setErr(null);
    api
      .deleteCapture(item.id)
      .then(() => {
        qc.invalidateQueries({ queryKey: ["captures"] });
        qc.invalidateQueries({ queryKey: ["categories"] });
      })
      .catch(() => setErr("Couldn't delete — please try again."));
  };

  return (
    <>
      <header className="header">
        <div className="col">
          <div className="header-inner">
            <button className="back-link" onClick={() => navigate("/")}>
              <Icon.back /> timeline
            </button>
            <div style={{ flex: 1 }} />
            <Wordmark />
          </div>
        </div>
      </header>
      <main className="col" style={{ paddingTop: 24, paddingBottom: 96 }}>
        <div className="date-marker">archived</div>
        {err ? (
          <p className="edit-error" role="alert">
            {err}
          </p>
        ) : null}
        {q.isLoading ? null : items.length === 0 ? (
          q.isError ? null : (
            <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Nothing archived.</p>
          )
        ) : (
          <ul className="row-list">
            {items.map((item) => (
              <Row key={item.id} item={item} query="" onDelete={remove} />
            ))}
          </ul>
        )}
        <LoadMore
          hasNextPage={q.hasNextPage}
          isFetchingNextPage={q.isFetchingNextPage}
          isError={q.isError}
          fetchNextPage={q.fetchNextPage}
        />
      </main>
    </>
  );
}
